import { Bell } from "lucide-react";
import React, { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "../../lib/utils";
import { Badge } from "../ui/common/badge";
import { Button } from "../ui/common/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/common/popover";

interface NotificationProps {
  children?: ReactNode;
  content?: ReactNode;
  notification?: any;
  onClick?: any;
}

// Only ever used from the header, so this was rewritten in place on
// Tailwind/Radix (Popover) instead of the previous Semantic UI Menu/Grid +
// hand-rolled PopOver - the read/unread/"View All" behaviour is unchanged.
export const NotificationDropdown: React.FC<NotificationProps> = ({ notification, onClick }) => {
  const navigate = useNavigate();
  const notificationList = notification?.list || [];
  const unreadCount = notification?.unReadCount || 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className="tw-relative tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-text-muted-foreground tw-transition-colors hover:tw-bg-muted hover:tw-text-foreground"
        >
          <Bell className="tw-h-5 tw-w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="tw-absolute -tw-right-1 -tw-top-1 tw-h-4 tw-min-w-4 tw-justify-center tw-rounded-full tw-border-0 tw-px-1 tw-text-[10px] tw-leading-none"
            >
              {unreadCount}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="tw-w-96 tw-p-0">
        <div className="tw-border-b tw-border-border tw-px-4 tw-py-3">
          <p className="tw-text-center tw-text-base tw-font-medium tw-text-gray-900">
            Your Notification
          </p>
        </div>

        <div className="tw-max-h-[420px] tw-overflow-y-auto">
          {notificationList.length === 0 ? (
            <p className="tw-px-4 tw-py-6 tw-text-center tw-text-sm tw-text-gray-500">
              No notifications yet.
            </p>
          ) : (
            notificationList.map((item: any) => {
              const formattedDate = new Date(item.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
              });

              return (
                <div
                  key={item._id}
                  onClick={() => onClick(item?._id)}
                  className={cn(
                    "tw-cursor-pointer tw-border-b tw-border-border tw-px-4 tw-py-3 tw-transition-colors hover:tw-bg-muted",
                    item.isRead ? "tw-bg-white" : "tw-bg-muted/50"
                  )}
                >
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                    <p className="tw-text-sm tw-font-normal tw-text-gray-900">{item.title}</p>
                    <p className="tw-shrink-0 tw-text-xs tw-text-gray-500">{formattedDate}</p>
                  </div>
                  <p className="tw-mt-1 tw-text-sm tw-text-gray-600">{item.body}</p>
                </div>
              );
            })
          )}
        </div>

        <div className="tw-border-t tw-border-border tw-px-4 tw-py-3 tw-text-center">
          <Button
            variant="outline"
            size="sm"
            className="hover:tw-bg-muted hover:tw-text-foreground"
            onClick={() => navigate("/notification")}
          >
            View All
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
